import React, { useEffect, useRef, useState } from 'react';
import { HiOutlineDocumentAdd, HiOutlineDuplicate, HiOutlineLink, HiOutlinePlus, HiOutlineX } from 'react-icons/hi';
import TextEdit from '../../../../../molecules/TextEdit';
import { Template } from '../../../../../../types/communication';
import { isDraftId } from '../studioDraft';

interface TemplateTabsProps {
    templates: Template[];
    activeId: string | null;
    disabled: boolean;
    canLink: boolean;
    onSelect: (id: string) => void;
    onRename: (id: string, name: string) => void;
    onDraftNew: () => void;
    onDuplicate: (id: string) => void;
    onLinkExisting: () => void;
    onRemove: (id: string) => void;
}

const TemplateTabs: React.FC<TemplateTabsProps> = ({
    templates,
    activeId,
    disabled,
    canLink,
    onSelect,
    onRename,
    onDraftNew,
    onDuplicate,
    onLinkExisting,
    onRemove,
}) => {
    const [menuOpen, setMenuOpen] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const menuRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!menuOpen) return undefined;
        const close = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) setMenuOpen(false);
        };
        document.addEventListener('mousedown', close);
        return () => document.removeEventListener('mousedown', close);
    }, [menuOpen]);

    const menuItem = (icon: React.ReactNode, title: string, text: string, onClick: () => void, enabled = true) => (
        <button
            type="button"
            disabled={!enabled}
            onClick={() => {
                onClick();
                setMenuOpen(false);
            }}
            className="w-full flex items-start gap-2 px-3 py-2 text-left hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed"
        >
            <span className="mt-0.5 text-teal-600 dark:text-teal-300">{icon}</span>
            <span>
                <span className="block text-sm text-gray-800 dark:text-gray-100">{title}</span>
                <span className="block text-[11px] text-gray-400">{text}</span>
            </span>
        </button>
    );

    return (
        <div className="flex items-end gap-1 border-b border-gray-200 dark:border-slate-700 mb-4">
            <div className="flex items-end gap-1 min-w-0 overflow-x-auto overflow-y-hidden [scrollbar-width:thin]">
                {templates.map((t) => {
                    const active = t.id === activeId;
                    return (
                        <div
                            key={t.id}
                            className={`group flex items-center gap-1.5 px-3 py-2 border-b-2 text-sm whitespace-nowrap cursor-pointer ${
                                active
                                    ? 'border-teal-500 text-teal-800 dark:text-teal-200 font-medium'
                                    : 'border-transparent text-gray-500 hover:text-gray-800 dark:hover:text-gray-200'
                            }`}
                            onClick={() => onSelect(t.id)}
                            onDoubleClick={() => !disabled && setEditingId(t.id)}
                            title={disabled ? t.name : 'Clique duas vezes para renomear'}
                        >
                            {editingId === t.id ? (
                                <div className="w-64" onClick={(e) => e.stopPropagation()}>
                                    <TextEdit
                                        id={`rename-${t.id}`}
                                        label="Nome do modelo"
                                        autoFocus
                                        defaultValue={t.name}
                                        onBlur={(e) => {
                                            if (e.target.value.trim()) onRename(t.id, e.target.value.trim());
                                            setEditingId(null);
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                                            if (e.key === 'Escape') setEditingId(null);
                                        }}
                                    />
                                </div>
                            ) : (
                                <span className="font-mono text-xs">{t.name}</span>
                            )}
                            {isDraftId(t.id) && (
                                <span className="rounded bg-amber-100 text-amber-800 px-1 text-[10px] uppercase tracking-wide">novo</span>
                            )}
                            {!disabled && templates.length > 1 && (
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (window.confirm(`Tirar "${t.name}" desta composição? O modelo fica livre para outra composição.`)) onRemove(t.id);
                                    }}
                                    className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-600"
                                    aria-label="Desvincular modelo"
                                    title="Desvincular modelo"
                                >
                                    <HiOutlineX size={14} />
                                </button>
                            )}
                        </div>
                    );
                })}
            </div>
            {!disabled && (
                <div ref={menuRef} className="relative ml-1 shrink-0 pb-1.5">
                    <button
                        type="button"
                        onClick={() => setMenuOpen((o) => !o)}
                        className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-teal-700 dark:text-teal-300 hover:bg-teal-50 dark:hover:bg-teal-900/30"
                    >
                        <HiOutlinePlus size={14} /> Modelo
                    </button>
                    {menuOpen && (
                        <div className="absolute z-30 left-0 mt-1 w-72 rounded-lg border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800 shadow-xl py-1">
                            {menuItem(<HiOutlineDocumentAdd size={16} />, 'Redigir novo', 'Começa um modelo em branco nesta composição', onDraftNew)}
                            {menuItem(
                                <HiOutlineDuplicate size={16} />,
                                'Duplicar o atual',
                                'Cria uma variação a partir deste modelo',
                                () => activeId && onDuplicate(activeId),
                                Boolean(activeId),
                            )}
                            {menuItem(
                                <HiOutlineLink size={16} />,
                                'Usar um existente',
                                canLink ? 'Vincula um modelo que ainda não está em nenhuma composição' : 'Não há modelos livres deste canal',
                                onLinkExisting,
                                canLink,
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default TemplateTabs;
